import { useCallback, useEffect, useRef, useState } from 'react';

export interface IdleTimerOptions {
    timeout: number; // in milliseconds
    promptTimeout?: number; // warning timeout before logout in milliseconds
    onIdle?: () => void;
    onActive?: () => void;
    onPrompt?: () => void;
    events?: string[];
    element?: Document | Element;
    startOnMount?: boolean;
    crossTab?: boolean;
    lockOnPrompt?: boolean; // Lock activity detection when warning appears
}

export interface IdleTimerApi {
    start: () => void;
    reset: () => void;
    pause: () => void;
    resume: () => void;
    isIdle: () => boolean;
    isPrompted: () => boolean;
    getTimeRemaining: () => number;
}

const DEFAULT_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'wheel'
];

const DEFAULT_ELEMENT = document;
const DEFAULT_TIMEOUT = 1000 * 60 * 15; // 15 minutes
const DEFAULT_PROMPT_TIMEOUT = 1000 * 60 * 1; // 2 minutes warning


export const useIdleTimer = (options: IdleTimerOptions): IdleTimerApi => {
    const {
        timeout = DEFAULT_TIMEOUT,
        promptTimeout = DEFAULT_PROMPT_TIMEOUT,
        onIdle,
        onActive,
        onPrompt,
        events = DEFAULT_EVENTS,
        element = DEFAULT_ELEMENT,
        startOnMount = true,
        crossTab = false,
        lockOnPrompt = false
    } = options;

    // State
    const [idle, setIdle] = useState(false);
    const [prompted, setPrompted] = useState(false);

    // Refs
    const startTime = useRef<number | null>(null);
    const tId = useRef<NodeJS.Timeout | null>(null);
    const promptId = useRef<NodeJS.Timeout | null>(null);
    const eventsBound = useRef(false);
    const paused = useRef(false);
    const locked = useRef(false); // Track if activities should be ignored

    // Cross tab state
    const tabId = useRef(Math.random().toString(36).substr(2, 9));

    const clearTimers = useCallback(() => {
        // Clear idle timeout
        if (tId.current) {
            clearTimeout(tId.current);
            tId.current = null;
        }
        // Clear prompt timeout
        if (promptId.current) {
            clearTimeout(promptId.current);
            promptId.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        if (paused.current) return;

        clearTimers();

        // Reset state
        setIdle(false);
        setPrompted(false);
        startTime.current = Date.now();
        locked.current = false; // Always unlock when resetting

        // Set prompt timeout if specified
        if (promptTimeout && promptTimeout < timeout) {
            const promptDelay = timeout - promptTimeout;
            promptId.current = setTimeout(() => {
                if (!paused.current) {
                    setPrompted(true);
                    if (lockOnPrompt) {
                        locked.current = true;
                    }
                    onPrompt?.();
                }
            }, promptDelay);
        }

        // Set idle timeout
        tId.current = setTimeout(() => {
            if (!paused.current) {
                setIdle(true);
                setPrompted(false);
                locked.current = false; // Unlock when going idle
                onIdle?.();
            }
        }, timeout);
    }, [timeout, promptTimeout, onIdle, onPrompt, clearTimers, lockOnPrompt]);

    const handleActivity = useCallback(() => {
        // Check if activities are locked
        if (locked.current) {
            return;
        }
        
        if (idle && onActive) {
            setIdle(false);
            onActive();
        }
        reset();
    }, [idle, onActive, reset]);

    const bindEvents = useCallback(() => {
        if (eventsBound.current) return;

        events.forEach(event => {
            element.addEventListener(event, handleActivity, {
                passive: true,
                capture: true
            });
        });

        eventsBound.current = true;
    }, [events, element, handleActivity]);

    const unbindEvents = useCallback(() => {
        if (!eventsBound.current) return;

        events.forEach(event => {
            element.removeEventListener(event, handleActivity, true);
        });

        eventsBound.current = false;
    }, [events, element, handleActivity]);

    const start = useCallback(() => {
        paused.current = false;
        bindEvents();
        reset();
    }, [bindEvents, reset]);

    const pause = useCallback(() => {
        paused.current = true;
        unbindEvents();
        clearTimers();
    }, [unbindEvents, clearTimers]);

    const resume = useCallback(() => {
        start();
    }, [start]);

    const getTimeRemaining = useCallback(() => {
        if (!startTime.current || paused.current) {
            return timeout;
        }
        const elapsed = Date.now() - startTime.current;
        const remaining = Math.max(0, timeout - elapsed);

        return remaining;
    }, [timeout]);
    const isIdle = useCallback(() => idle, [idle]);
    const isPrompted = useCallback(() => prompted, [prompted]);

    // // Handle page visibility changes
    // useEffect(() => {
    //     const handleVisibilityChange = () => {
    //         const visible = !document.hidden;

    //         if (visible && !paused.current) {
    //             handleActivity();
    //         }
    //     };

    //     document.addEventListener('visibilitychange', handleVisibilityChange);
    //     return () => {
    //         document.removeEventListener('visibilitychange', handleVisibilityChange);
    //     };
    // }, [handleActivity]);

    // Cross-tab synchronization
    useEffect(() => {
        if (!crossTab) return;

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'idle-timer-activity' && e.newValue !== tabId.current) {
                handleActivity();
            }
        };

        const broadcastActivity = () => {
            localStorage.setItem('idle-timer-activity', tabId.current);
        };

        window.addEventListener('storage', handleStorageChange);

        // Broadcast activity on any user interaction
        events.forEach(event => {
            element.addEventListener(event, broadcastActivity, { passive: true });
        });

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            events.forEach(event => {
                element.removeEventListener(event, broadcastActivity);
            });
        };
    }, [crossTab, events, element, handleActivity]);

    // Start timer on mount if specified
    useEffect(() => {
        if (startOnMount) {
            start();
        }

        return () => {
            pause();
        };
    }, [startOnMount, start, pause]);

    return {
        start,
        reset,
        pause,
        resume,
        isIdle,
        isPrompted,
        getTimeRemaining
    };
};
